import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2, FileText } from "lucide-react";
import DOMPurify from "dompurify";
import { stemProjectApi } from "../../services/stemProjectApi";

export const SafeHtml = ({ html, className }) => (
  <div
    className={`sms-stage-view-html ck-content ${className ?? ""}`}
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || "") }}
  />
);

const TURN_MS = 750;

// Keep the part of a polygon where (W - x) + y is >= d (keepAbove) or <= d.
// The page is folded along that 45-degree line, starting at the top-right corner.
const clipByFold = (poly, d, W, keepAbove) => {
  const val = ([x, y]) => (W - x + y - d) * (keepAbove ? 1 : -1);
  const out = [];
  poly.forEach((p, i) => {
    const q = poly[(i + 1) % poly.length];
    const vp = val(p);
    const vq = val(q);
    if (vp >= 0) out.push(p);
    if ((vp >= 0) !== (vq >= 0)) {
      const t = vp / (vp - vq);
      out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
    }
  });
  return out;
};

const toPath = (pts) =>
  pts.length < 3 ? "polygon(0 0, 0 0, 0 0)" : `polygon(${pts.map(([x, y]) => `${x}px ${y}px`).join(",")})`;

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/**
 * PowerPoint-style page navigator.
 * Fetches all pages from the API once, then navigates locally with a book page-turn animation.
 */
export const PagedNoteViewer = ({ stageId, noteId, isCompleted, onCompleteStage, onNextStage, isLastStage }) => {
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(0);
  const [turn, setTurn] = useState(null); // { from, to, dir }
  const timer = useRef(null);
  const bookRef = useRef(null);
  const leafRef = useRef(null);
  const flapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!stageId || !noteId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    clearTimeout(timer.current);
    setPage(0);
    setTurn(null);

    console.log("[PagedNoteViewer] Fetching pages:", { stageId, noteId });
    stemProjectApi
      .getStageNotePages(stageId, noteId)
      .then((data) => {
        if (!cancelled) {
          console.log("[PagedNoteViewer] Pages received:", data);
          const sorted = [...(data || [])].sort((a, b) => a.pageNumber - b.pageNumber);
          setPages(sorted);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[PagedNoteViewer] Error fetching pages:", err);
          setError(err.message || "Failed to load pages");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [stageId, noteId]);

  const last = pages.length - 1;

  // Drive the corner peel: the turning leaf is clipped along a moving fold line
  // and the folded-over corner (back of the paper) is drawn as its mirror image.
  useLayoutEffect(() => {
    if (!turn || !bookRef.current) return undefined;
    const W = bookRef.current.offsetWidth;
    const H = bookRef.current.offsetHeight;
    const rect = [[0, 0], [W, 0], [W, H], [0, H]];
    const start = performance.now();
    let raf;
    const draw = (now) => {
      const p = Math.min(1, (now - start) / TURN_MS);
      const e = easeInOut(p);
      const d = (turn.dir === "next" ? e : 1 - e) * (W + H);
      if (leafRef.current) leafRef.current.style.clipPath = toPath(clipByFold(rect, d, W, true));
      if (flapRef.current) {
        const cut = clipByFold(rect, d, W, false).map(([x, y]) => {
          const g = W - x + y - d;
          return [x + g, y - g];
        });
        flapRef.current.style.clipPath = toPath(cut);
      }
      if (p < 1) raf = requestAnimationFrame(draw);
    };
    draw(start);
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [turn]);

  const go = (to) => {
    if (turn || to < 0 || to > last || to === page) return; // ignore clicks while a page is turning
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setPage(to);
      return;
    }
    setTurn({ from: page, to, dir: to > page ? "next" : "prev" });
    setPage(to);
    timer.current = setTimeout(() => setTurn(null), TURN_MS);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "30px", gap: "8px", color: "var(--mute)" }}>
        <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
        <span style={{ fontSize: "12.5px" }}>Loading pages…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "12px 16px", background: "var(--red-soft)", borderRadius: "8px", color: "var(--red-ink)", fontSize: "12.5px" }}>
        {error}
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div style={{ padding: "16px", textAlign: "center", color: "var(--mute)", fontSize: "12.5px" }}>
        No pages available for this note.
      </div>
    );
  }

  const isLastPage = page >= last;

  // Forward: base = destination, turning leaf = page being left. Back: the reverse.
  const baseIdx = turn ? (turn.dir === "next" ? turn.to : turn.from) : page;
  const flipIdx = turn ? (turn.dir === "next" ? turn.from : turn.to) : null;

  return (
    <div className="sms-ppt-viewer">
      {/* Page Content */}
      <div className="sms-ppt-content book" aria-live="polite" ref={bookRef}>
        <div className="leaf leaf--base">
          <SafeHtml html={pages[baseIdx].body} className="sms-note-page-content" />
        </div>
        {turn && (
          <div className="leaf-wrap" aria-hidden="true">
            <div className="leaf leaf--turn" ref={leafRef}>
              <SafeHtml html={pages[flipIdx].body} className="sms-note-page-content" />
            </div>
            <div className="leaf-flap" ref={flapRef} />
          </div>
        )}
      </div>

      {/* Bottom nav: Prev | dots | Next / Next Stage */}
      <div className="sms-ppt-nav">
        <button
          type="button"
          className="sms-ppt-nav-btn"
          onClick={() => go(page - 1)}
          disabled={page === 0 || !!turn}
          title="Previous page"
        >
          <ChevronLeft size={14} />
          <span>Previous</span>
        </button>

        <div className="sms-ppt-dots">
          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`sms-ppt-dot ${i === page ? "active" : ""}`}
              onClick={() => go(i)}
              title={`Page ${i + 1}`}
            />
          ))}
        </div>

        {isLastPage ? (
          <button
            type="button"
            className="sms-btn-complete-stage"
            onClick={() => {
              if (onCompleteStage) onCompleteStage();
              if (onNextStage) onNextStage();
            }}
          >
            <span>{isLastStage ? "Complete" : "Next Stage"}</span>
            <ChevronRight size={14} />
          </button>
        ) : (
          <button
            type="button"
            className="sms-ppt-nav-btn"
            onClick={() => go(page + 1)}
            disabled={!!turn}
            title="Next page"
          >
            <span>Next</span>
            <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default function StageEditorView({
  stage,
  isCompleted,
  onCompleteStage,
  onNextStage,
  isLastStage,
}) {
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    console.log("[StageEditorView] Fetching notes for stage:", {
      stageId: stage.id,
      stageName: stage.name,
    });

    stemProjectApi
      .getStageNotes(stage.id)
      .then((data) => {
        if (!cancelled) {
          const list = data || [];
          console.log("[StageEditorView] Received notes data:", list);
          setNotes(list);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[StageEditorView] Error loading notes:", err);
          setError(err.message || "Failed to load content notes");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [stage.id, stage.name]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "35px", color: "var(--mute)" }}>
        <Loader2 size={22} style={{ animation: "spin 1s linear infinite", margin: "0 auto 8px" }} />
        <p style={{ fontSize: "12.5px" }}>Loading saved content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "14px", borderRadius: "8px", background: "var(--red-soft)", color: "var(--red-ink)", fontSize: "12.5px" }}>
        {error}
      </div>
    );
  }

  if (!notes || notes.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "35px",
          background: "var(--bg)",
          borderRadius: "8px",
          border: "1px dashed var(--line)",
          color: "var(--mute)",
        }}
      >
        <FileText size={26} color="var(--mute)" style={{ margin: "0 auto 8px" }} />
        <p style={{ fontSize: "13px", fontWeight: "600", margin: "0 0 4px" }}>
          Nothing saved yet.
        </p>
        <span style={{ fontSize: "11.5px", color: "var(--mute)" }}>
          Learning materials and editor notes for this stage will be displayed here.
        </span>
      </div>
    );
  }

  return (
    <div className="sms-stage-editor-wrapper">
      <div className="sms-ppt-viewer-wrapper">
        <PagedNoteViewer
          stageId={stage.id}
          noteId={notes[0].id}
          isCompleted={isCompleted}
          onCompleteStage={onCompleteStage}
          onNextStage={onNextStage}
          isLastStage={isLastStage}
        />
      </div>

      <style>{`
        .sms-stage-editor-wrapper {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .sms-ppt-viewer-wrapper {
          width: 100%;
        }

        .sms-ppt-viewer {
          display: flex;
          flex-direction: column;
          width: 100%;
          background: var(--white);
          border: 1px solid var(--line);
          border-radius: 10px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }

        .sms-ppt-content {
          flex: 1;
          min-width: 0;
        }

        .sms-ppt-viewer .sms-note-page-content {
          padding: 28px 32px 24px;
          font-size: 14.5px;
          line-height: 1.75;
          color: var(--ink);
          overflow-wrap: anywhere;
        }

        .sms-ppt-viewer .sms-note-page-content > :first-child {
          margin-top: 0;
        }

        .sms-ppt-viewer .sms-note-page-content h1 {
          font-size: 22px;
          font-weight: 700;
          line-height: 1.3;
          color: var(--blue);
          margin: 0 0 14px;
          padding-bottom: 10px;
          border-bottom: 2px solid var(--blue-line);
        }

        .sms-ppt-viewer .sms-note-page-content h2 {
          font-size: 16.5px;
          font-weight: 700;
          line-height: 1.35;
          color: var(--ink);
          margin: 24px 0 6px;
          padding-left: 10px;
          border-left: 3px solid var(--blue-2);
        }

        .sms-ppt-viewer .sms-note-page-content h3,
        .sms-ppt-viewer .sms-note-page-content h4 {
          font-size: 14.5px;
          font-weight: 700;
          color: var(--blue);
          margin: 18px 0 4px;
        }

        .sms-ppt-viewer .sms-note-page-content p {
          margin: 0 0 10px;
          color: var(--ink);
        }

        .sms-ppt-viewer .sms-note-page-content ul,
        .sms-ppt-viewer .sms-note-page-content ol {
          margin: 0 0 10px;
          padding-left: 22px;
        }

        .sms-ppt-viewer .sms-note-page-content li {
          margin-bottom: 4px;
        }

        .sms-note-page-content img {
          max-width: 60%;
          max-height: 260px;
          width: auto;
          height: auto;
          object-fit: contain;
          border-radius: 6px;
          margin: 12px auto;
          display: block;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }

        .sms-note-page-content table {
          display: block;
          max-width: 100%;
          overflow-x: auto;
          border-collapse: collapse;
          margin: 10px 0;
        }

        .sms-note-page-content td,
        .sms-note-page-content th {
          padding: 6px 10px;
          border: 1px solid var(--line);
        }

        /* Bottom navigation bar */
        .sms-ppt-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 8px 14px;
          border-top: 1px solid var(--line);
          background: var(--bg);
          border-radius: 0 0 10px 10px;
        }

        /* Dots */
        .sms-ppt-dots {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .sms-ppt-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 2px solid var(--line);
          background: var(--white);
          padding: 0;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
        }

        .sms-ppt-dot:hover {
          border-color: var(--blue-2);
          background: var(--blue-line);
        }

        .sms-ppt-dot.active {
          background: var(--blue-2);
          border-color: var(--blue-2);
          transform: scale(1.25);
        }

        /* Prev / Next buttons */
        .sms-ppt-nav-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 12px;
          font-size: 12px;
          font-weight: 600;
          color: var(--ink);
          background: var(--white);
          border: 1px solid var(--line);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
          flex-shrink: 0;
        }

        .sms-ppt-nav-btn:not(:disabled):hover {
          border-color: var(--blue-2);
          color: var(--blue-2);
        }

        .sms-ppt-nav-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        /* Complete / Next Stage button */
        .sms-btn-complete-stage {
          background: linear-gradient(var(--blue), var(--blue-2));
          color: var(--white);
          border: none;
          border-radius: 6px;
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25);
          transition: background 0.15s ease;
          flex-shrink: 0;
        }

        .sms-btn-complete-stage:hover {
          background: var(--blue-2);
        }

        /* Book page-turn (corner peel) */
        .book { position: relative; width: 100%; }

        .leaf {
          background: #fffdf8;
          border: 1px solid #ece4d2;
          border-radius: 6px 14px 14px 6px;
          box-shadow: 0 8px 24px rgba(15,23,42,.1), inset 8px 0 14px -10px rgba(120,90,40,.35);
        }

        .leaf-wrap {
          position: absolute; inset: 0;
          z-index: 2;
          pointer-events: none;
          filter: drop-shadow(-3px 3px 6px rgba(15,23,42,.28));
        }

        .leaf--turn { position: absolute; inset: 0; box-shadow: none; will-change: clip-path; }

        /* back of the folded corner */
        .leaf-flap {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, #f3ead6 0%, #fffdf8 70%);
          will-change: clip-path;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
